import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import './History.css';

interface HistoryItem {
  id: string;
  siret: string;
  company_name: string;
  score: number | null;
  risk_level: string;
  checked_at: string;
  loading?: boolean;
  progress?: number;
  report_id?: string;
}

interface HistoryProps {
  onViewReport: (siretOrReportId: string, isReportId?: boolean) => void;
  activeScans?: HistoryItem[];
  onStartScan?: (scan: any) => void;
  onUpdateScan?: (id: string, updates: any) => void;
  onRemoveScan?: (id: string) => void;
  onGoToSearch?: (query: string) => void;
  userEmail?: string;
  quotaLeft?: number;
}

function History({ onViewReport, activeScans = [], onStartScan, onUpdateScan, onRemoveScan, onGoToSearch, userEmail, quotaLeft = 0 }: HistoryProps) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | 'all' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<'company_name' | 'score' | 'checked_at'>('checked_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [companySearchError, setCompanySearchError] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [tempSearchQuery, setTempSearchQuery] = useState('');
  const [showQuotaTooltip, setShowQuotaTooltip] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const path = userEmail
        ? `/api/solvability/history?userEmail=${encodeURIComponent(userEmail)}`
        : `/api/solvability/history`;
      const response = await apiFetch(path);
      const data = await response.json();
      if (data.success) {
        setHistory(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    }
  };

  useEffect(() => {
    const interval = setInterval(loadHistory, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDeleteClick = (target: string | 'all') => {
    setDeleteTarget(target);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget === 'all') {
        await apiFetch(`/api/solvability/history`, {
          method: 'DELETE'
        });
        setHistory([]);
      } else if (deleteTarget) {
        await apiFetch(`/api/solvability/report/${deleteTarget}`, {
          method: 'DELETE'
        });
        const filtered = history.filter(item => item.id !== deleteTarget);
        setHistory(filtered);
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const getRiskBadge = (risk: string) => {
    const badges: any = {
      low: { label: 'Faible', color: '#4caf50' },
      medium: { label: 'Moyen', color: '#ff9800' },
      high: { label: 'Élevé', color: '#ff5722' },
      very_high: { label: 'Très élevé', color: '#f44336' },
      critical: { label: 'Critique', color: '#d32f2f' },
      unknown: { label: 'Inconnu', color: '#999' }
    };
    return badges[risk] || badges.unknown;
  };

  const getScoreColor = (score: number | null) => {
    if (!score || score === 0) return '#f44336';
    if (score <= 5) return '#f44336';
    if (score <= 11) return '#ff9800';
    if (score <= 15) return '#cddc39';
    if (score <= 19) return '#4caf50';
    return '#4caf50';
  };

  const handleSort = (column: 'company_name' | 'score' | 'checked_at') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredHistory = history.filter(item =>
    item.company_name.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
    item.siret.includes(modalSearchQuery)
  );

  const sortedHistory = [...filteredHistory].sort((a, b) => {
    let comparison = 0;
    if (sortColumn === 'company_name') {
      comparison = a.company_name.localeCompare(b.company_name);
    } else if (sortColumn === 'score') {
      comparison = (a.score || 0) - (b.score || 0);
    } else if (sortColumn === 'checked_at') {
      comparison = new Date(a.checked_at).getTime() - new Date(b.checked_at).getTime();
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const allItems = [...activeScans, ...sortedHistory];

  const handleCompanySearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (quotaLeft !== undefined && quotaLeft <= 0) {
      setCompanySearchError('Quota de vérifications épuisé');
      return;
    }

    if (!companySearchQuery.trim()) {
      setCompanySearchError('Veuillez saisir un nom d\'entreprise');
      return;
    }

    setCompanySearchLoading(true);
    setCompanySearchError('');

    try {
      const response = await apiFetch(`/api/solvability/search?query=${encodeURIComponent(companySearchQuery)}&page=1`);
      const data = await response.json();

      if (data.success) {
        if (data.data.companies.length === 1) {
          // Une seule entreprise trouvée, scan direct
          const company = data.data.companies[0];
          setCompanySearchLoading(false);
          handleCheckCompany(company.siret, company.nom);
        } else if (data.data.companies.length > 1) {
          // Plusieurs entreprises, rediriger vers la page de recherche
          setCompanySearchLoading(false);
          if (onGoToSearch) {
            onGoToSearch(companySearchQuery);
          }
        } else {
          setCompanySearchError('Aucune entreprise trouvée');
          setCompanySearchLoading(false);
        }
      } else {
        setCompanySearchError(data.message || 'Erreur lors de la recherche');
        setCompanySearchLoading(false);
      }
    } catch (err: any) {
      setCompanySearchError('Erreur de connexion au serveur: ' + err.message);
      setCompanySearchLoading(false);
    }
  };

  const handleCheckCompany = async (siret: string, companyName: string) => {
    setCompanySearchQuery('');

    // Vérifier si un scan avec ce SIRET est déjà en cours
    if (activeScans.some(scan => scan.siret === siret && scan.loading)) {
      return;
    }

    const scanId = Date.now().toString();
    const scan = {
      id: scanId,
      siret: siret,
      company_name: companyName,
      score: null,
      risk_level: 'unknown',
      checked_at: new Date().toISOString(),
      loading: true,
      progress: 0
    };

    if (onStartScan) {
      onStartScan(scan);
    }

    // Simuler la progression
    let currentProgress = 0;
    const progressInterval = setInterval(() => {
      if (onUpdateScan) {
        currentProgress = Math.min(90, currentProgress + 1.5);
        onUpdateScan(scanId, { progress: currentProgress });
      }
    }, 100);

    try {
      const checkPath = userEmail
        ? `/api/solvability/check/${siret}?userEmail=${encodeURIComponent(userEmail)}`
        : `/api/solvability/check/${siret}`;
      const response = await apiFetch(checkPath);
      const data = await response.json();

      clearInterval(progressInterval);

      if (data.success) {
        // Animer progressivement jusqu'à 100%
        const animateToHundred = setInterval(() => {
          if (onUpdateScan) {
            currentProgress = Math.min(100, currentProgress + 5);
            onUpdateScan(scanId, { progress: currentProgress });

            if (currentProgress >= 100) {
              clearInterval(animateToHundred);

              const historyItem = {
                id: scanId,
                siret: siret,
                company_name: data.data.company_info?.company_name || companyName,
                score: data.data.financial_score,
                risk_level: data.data.risk_level,
                checked_at: new Date().toISOString(),
                report_id: data.report_id
              };

              if (onRemoveScan) {
                onRemoveScan(scanId);
              }

              loadHistory();
            }
          }
        }, 50);
      } else if (data.error === 'QUOTA_EXCEEDED') {
        if (onRemoveScan) {
          onRemoveScan(scanId);
        }
        setCompanySearchError(data.message || 'Quota de vérifications épuisé');
      } else {
        if (onRemoveScan) {
          onRemoveScan(scanId);
        }
        setCompanySearchError(data.message || 'Erreur lors de la vérification');
      }
    } catch (err: any) {
      clearInterval(progressInterval);
      if (onRemoveScan) {
        onRemoveScan(scanId);
      }
      setCompanySearchError('Erreur de connexion au serveur');
    }
  };

  return (
    <div className="history">
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmation</h3>
            <p>
              {deleteTarget === 'all'
                ? 'Voulez-vous vraiment supprimer tout l\'historique ?'
                : 'Voulez-vous vraiment supprimer cette vérification ?'}
            </p>
            <div className="modal-actions">
              <button onClick={cancelDelete} className="btn-cancel">
                Annuler
              </button>
              <button onClick={confirmDelete} className="btn-confirm">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {history.length === 0 && activeScans.length === 0 ? (
        <div className="no-history">
          <p>Aucune vérification effectuée pour le moment</p>
        </div>
      ) : (
        <>
          {showSearchModal && (
            <div className="modal-overlay" onClick={() => {
              setShowSearchModal(false);
              setTempSearchQuery('');
            }}>
              <div className="modal-content search-modal" onClick={(e) => e.stopPropagation()}>
                <h2>Rechercher dans l'historique</h2>
                <input
                  type="text"
                  placeholder="Nom d'entreprise ou SIRET..."
                  value={tempSearchQuery}
                  onChange={(e) => setTempSearchQuery(e.target.value)}
                  className="modal-search-input"
                  autoFocus
                />
                <div className="modal-actions">
                  <button type="button" onClick={() => {
                    setShowSearchModal(false);
                    setTempSearchQuery('');
                  }} className="btn-cancel">
                    Annuler
                  </button>
                  <button type="button" onClick={() => {
                    setModalSearchQuery(tempSearchQuery);
                    setShowSearchModal(false);
                  }} className="btn-search-confirm">
                    Rechercher
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="history-list-lines">
            <div className="history-header-line">
              <span className="header-company sortable" onClick={() => handleSort('company_name')}>
                Nom entreprise {sortColumn === 'company_name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </span>
              <span className="header-siret">SIRET</span>
              <span className="header-progress">Progression</span>
              <span className="header-score sortable" onClick={() => handleSort('score')}>
                Note {sortColumn === 'score' && (sortDirection === 'asc' ? '↑' : '↓')}
              </span>
              <span className="header-date sortable" onClick={() => handleSort('checked_at')}>
                Date {sortColumn === 'checked_at' && (sortDirection === 'asc' ? '↑' : '↓')}
              </span>
              <span className="header-actions">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                  <span>Actions</span>
                  <button className="header-icon-btn" onClick={(e) => {
                    e.stopPropagation();
                    setTempSearchQuery(modalSearchQuery);
                    setShowSearchModal(true);
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                  </button>
                  {history.length > 0 && (
                    <button className="header-icon-btn" onClick={() => handleDeleteClick('all')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  )}
                </div>
              </span>
            </div>
            {allItems.length === 0 ? (
              <div className="no-history">
                <p>Aucun résultat trouvé</p>
                {modalSearchQuery && (
                  <button onClick={() => setModalSearchQuery('')} className="btn-reset-search">
                    Réinitialiser
                  </button>
                )}
              </div>
            ) : (
              allItems.map((item) => (
                <div key={item.id} className="history-line">
                  <span className="line-company">{item.company_name}</span>
                  <span className="line-siret">{item.siret}</span>
                  <div className="line-progress-container">
                    <div className="line-progress-bar">
                      <div
                        className="line-progress-fill"
                        style={{ width: item.loading ? `${item.progress || 0}%` : '100%' }}
                      ></div>
                    </div>
                    {item.loading && (
                      <span className="line-progress-text">{Math.round(item.progress || 0)}%</span>
                    )}
                  </div>
                  {item.loading ? (
                    <>
                      <span className="line-empty"></span>
                      <span className="line-empty"></span>
                      <span className="line-empty"></span>
                    </>
                  ) : (
                    <>
                      {item.score === 20 ? (
                        <span className="line-score score-perfect">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                          20/20
                        </span>
                      ) : (
                        <span className="line-score" style={{ color: getScoreColor(item.score) }}>
                          {item.score || 0}/20
                        </span>
                      )}
                      <span className="line-date">
                        {new Date(item.checked_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <div className="line-actions">
                        <button onClick={() => item.report_id ? onViewReport(item.report_id, true) : onViewReport(item.siret)} className="line-view-btn">
                          Consulter
                        </button>
                        <button onClick={() => handleDeleteClick(item.id)} className="line-delete-btn">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default History;
